import { useEffect, useState } from 'react';
import { useRouteError, isRouteErrorResponse, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, RefreshCw, Home, Loader2 } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { CHUNK_RELOAD_KEY } from '@/utils/lazyWithRetry';

const CHUNK_ERROR_RETRY_KEY = 'chunk_error_last_retry';
const CHUNK_ERROR_MAX_RETRIES = 2;
const CHUNK_ERROR_RETRY_WINDOW_MS = 30_000; // 30s window to detect loops

/**
 * Route Error Boundary component for React Router
 * Uses useRouteError() hook to catch route-level errors
 *
 * For chunk load errors (stale deployment): auto-retries once via hard
 * navigation, then shows manual reload UI if the retry also fails.
 * This prevents production users from being permanently stuck.
 */
export function RouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();
  const { t } = useTranslation('errors');
  const isDev = import.meta.env.DEV;
  const [autoRetrying, setAutoRetrying] = useState(false);

  // Determine error details
  let errorMessage = t('route.defaultMessage');
  let errorStatus: number | undefined;
  let errorDetails: string | undefined;
  let isChunkLoadError = false;

  if (isRouteErrorResponse(error)) {
    errorStatus = error.status;
    errorMessage = error.statusText || errorMessage;
    errorDetails = error.data?.message || error.data;
  } else if (error instanceof Error) {
    errorMessage = error.message;
    errorDetails = error.stack;

    isChunkLoadError =
      errorMessage.includes('Failed to fetch dynamically imported module') ||
      errorMessage.includes('Importing a module script failed') ||
      errorMessage.includes('error loading dynamically imported module');

    if (isChunkLoadError) {
      errorMessage = t('route.chunkLoadMessage');
    }
  } else if (typeof error === 'string') {
    errorMessage = error;
  }

  console.error('RouteErrorBoundary caught an error:', error);

  // Auto-retry on chunk load errors (runs once on mount)
  useEffect(() => {
    if (!isChunkLoadError) return;

    // If lazyWithRetry already triggered a reload recently, skip auto-retry
    // to avoid looping. Just show the manual reload UI.
    const lazyReload = sessionStorage.getItem(CHUNK_RELOAD_KEY);
    if (lazyReload && Date.now() - Number(lazyReload) < 15_000) {
      console.warn('[ChunkLoadError] lazyWithRetry already reloaded recently, showing manual UI');
      return;
    }

    // Check retry history to prevent infinite loops
    const retryData = sessionStorage.getItem(CHUNK_ERROR_RETRY_KEY);
    let retryCount = 0;

    if (retryData) {
      try {
        const parsed = JSON.parse(retryData);
        const elapsed = Date.now() - parsed.timestamp;
        if (elapsed < CHUNK_ERROR_RETRY_WINDOW_MS) {
          retryCount = parsed.count;
        }
        // Outside window = stale, reset
      } catch {
        // Corrupted data, reset
      }
    }

    if (retryCount >= CHUNK_ERROR_MAX_RETRIES) {
      // Already retried enough — show manual UI, clear for next session
      console.warn('[ChunkLoadError] Max auto-retries reached, showing manual reload UI');
      sessionStorage.removeItem(CHUNK_ERROR_RETRY_KEY);
      return;
    }

    // Record this retry attempt
    sessionStorage.setItem(
      CHUNK_ERROR_RETRY_KEY,
      JSON.stringify({ count: retryCount + 1, timestamp: Date.now() })
    );

    console.warn(`[ChunkLoadError] Auto-retrying (attempt ${retryCount + 1}/${CHUNK_ERROR_MAX_RETRIES})...`);
    setAutoRetrying(true);

    // Small delay so the user sees something is happening
    const timer = setTimeout(() => {
      hardReload();
    }, 1000);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hardReload = () => {
    // Unregister all service workers so they don't serve stale cached HTML
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(r => r.unregister());
      });
    }
    // Cache-busting navigation — bypasses CDN and browser cache
    const url = new URL(window.location.href);
    url.searchParams.set('_cb', Date.now().toString());
    window.location.replace(url.toString());
  };

  const handleReload = () => {
    // Clear retry counter so manual reload gets fresh attempts
    sessionStorage.removeItem(CHUNK_ERROR_RETRY_KEY);
    hardReload();
  };

  const handleGoHome = () => {
    navigate('/dashboard');
  };

  // Auto-retry in progress — show spinner, not the error page
  if (autoRetrying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-base px-4">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 text-primary-500 animate-spin" />
          <p className="text-sm text-content-secondary">{t('route.chunkLoadMessage')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-base px-4">
      <div className="max-w-md w-full bg-surface-raised rounded-lg shadow-lg p-8">
        <div className="flex items-center justify-center w-16 h-16 mx-auto bg-red-100 dark:bg-red-900/30 rounded-full mb-4">
          <AlertTriangle className="h-8 w-8 text-red-600 dark:text-red-400" />
        </div>

        <h2 className="text-2xl font-bold text-content-primary text-center mb-2">
          {isChunkLoadError
            ? t('route.updateAvailable')
            : errorStatus
            ? t('route.errorWithStatus', { status: errorStatus })
            : t('route.title')}
        </h2>

        <p className="text-content-secondary text-center mb-6">
          {isChunkLoadError
            ? errorMessage
            : isDev
            ? errorMessage
            : t('route.productionMessage')}
        </p>

        {!isChunkLoadError && isDev && errorDetails && (
          <div className="mb-6 text-sm">
            <div className="bg-surface-base rounded p-4 overflow-auto max-h-64">
              <p className="font-mono text-xs text-red-600 dark:text-red-400 break-words mb-2">
                {errorMessage}
              </p>
              <pre className="font-mono text-xs text-content-secondary whitespace-pre-wrap">
                {errorDetails}
              </pre>
            </div>
          </div>
        )}

        {!isChunkLoadError && !isDev && errorDetails && (
          <details className="mb-6 text-sm">
            <summary className="cursor-pointer text-content-secondary font-medium mb-2">
              {t('route.errorDetails')}
            </summary>
            <div className="bg-surface-base rounded p-4 overflow-auto max-h-64">
              <p className="font-mono text-xs text-red-600 dark:text-red-400 break-words">
                {errorMessage}
              </p>
              {errorDetails && (
                <pre className="font-mono text-xs text-content-secondary mt-2 whitespace-pre-wrap">
                  {errorDetails}
                </pre>
              )}
            </div>
          </details>
        )}

        <div className="flex gap-3">
          {isChunkLoadError ? (
            <Button
              onClick={handleReload}
              className="w-full flex items-center justify-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              {t('route.reloadPage')}
            </Button>
          ) : (
            <>
              <Button
                onClick={handleGoHome}
                variant="outline"
                className="flex-1 flex items-center justify-center gap-2"
              >
                <Home className="h-4 w-4" />
                {t('route.goHome')}
              </Button>
              <Button
                onClick={handleReload}
                className="flex-1 flex items-center justify-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                {t('route.reloadPage')}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
